<i18n>
{
  "en": {
    "restaurantDataError": "Error loading restaurant data: {error}",
    "priceVerification": "Prices recommendations pending in {startDate} to {endDate}",
    "engineeringVerification": "Menu engineer recommendations pending in {startDate} to {endDate}",
    "newItems": "There are {0} products left to configure",
    "noManagement": "Some management actions might not be available for this restaurant",
    "restaurantSettingNotFound": "The restaurant does not have RestaurantSettings properly configured"
  },
  "es": {
    "restaurantDataError": "Error cargando datos del restaurante: {error}",
    "priceVerification": "Recomendaciones de precios pendientes en el periodo {startDate} a {endDate}",
    "engineeringVerification": "Recomendaciones de ingeniería de menú pendientes en el periodo {startDate} a {endDate}",
    "newItems": "Aún hay {0} productos sin configurar",
    "noManagement": "Algunas gestiones no están disponibles para este restaurante",
    "restaurantSettingNotFound": "El restaurante no tiene configurado los RestaurantSettings correctamente"
  }
}
</i18n>
<template>
  <router-view/>
</template>

<script>
import { createErrorNotification, createInfoNotification } from '@ulibs/unnotificationsqueue';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { MapActions, MapGetters } from '@core/helpers/decorators';

@Component
@MapActions(['addNotification', 'setLoading', 'saleZoneListRequest', 'predictionPeriodListRequest', 'masterSaleFormatListRequest',
  'serviceListRequest', 'saleRateListRequest', 'pointOfSaleSettingsRequest', 'optionListRequest',
  'resetPointOfSaleData', 'newProductsRequest', 'removeNotificationById', 'groupOfProductPricingListRequest'])
@MapGetters(['masterProductListCancelToken', 'isLoading', 'predictionPeriodList', 'nextPeriod', 'newProducts',
  'pointOfSaleSettings'])
export default class PointOfSale extends Vue {
  notificationIds = new Set()

  async created() {
    await this.loadRestaurantData();
  }

  async showNotificationIfNeeded() {
    await this.showPeriodNotificationsIfNeeded();
    await this.showNewProductsNotification();
    await this.showNoManagementNotification();
  }

  async showPeriodNotificationsIfNeeded() {
    await Promise.all(this.predictionPeriodList.filter((p) => !p.isPreviousPeriod).flatMap((period) => this.showSinglePeriodNotification(period)));
  }

  async showSinglePeriodNotification(period) {
    const periodLabelConfig = {
      startDate: period.startDateLocaleStr,
      endDate: period.endDateLocaleStr
    };
    const notif = [];
    if (!period.areProductPriceRecommendationsValidated) {
      notif.push(this.showPartialPeriodNotification('priceVerification', 'price', periodLabelConfig, period));
    }
    if (!period.areMenuEngineeringRecommendationsValidated) {
      notif.push(this.showPartialPeriodNotification('engineeringVerification', 'menu', periodLabelConfig, period));
    }
    return notif;
  }

  async showPartialPeriodNotification(labelStr, idStr, periodLabelConfig, period) {
    const label = this.$t(labelStr, periodLabelConfig);
    const id = `${idStr}${period.guid}`;
    const priceNotificationConfig = { id, persistent: true };
    const notification = createInfoNotification(label, priceNotificationConfig);
    this.notificationIds.add(id);
    return this.addNotification(notification);
  }

  async showNewProductsNotification() {
    if (this.newProducts > 0) {
      const label = this.$t('newItems', [this.newProducts]);
      const productNotificationConfig = { id: 'newItems', persistent: true };
      const notification = createInfoNotification(label, productNotificationConfig);
      this.notificationIds.add('newItems');
      await this.addNotification(notification);
    }
  }

  async showNoManagementNotification() {
    if (this.predictionPeriodList.length === 0 && !this.predictionPeriodList.cleared) {
      await this.addNotification(createInfoNotification(this.$t('noManagement'), {}, 3000));
    }
  }

  async removeRestaurantNotifications() {
    await Promise.all([...this.notificationIds.values()].map((id) => this.removeNotificationById(id)));
    this.notificationIds.clear();
  }

  async loadRestaurantData() {
    await this.setLoading(1);
    const posGuid = this.$route.params.pointOfSaleGuid;
    try {
      if (this.pointOfSaleSettings?.pointOfSale !== posGuid) {
        await this.resetPointOfSaleData();
        await Promise.all([
          this.optionListRequest(),
          this.predictionPeriodListRequest(posGuid),
          this.saleRateListRequest(posGuid),
          this.serviceListRequest(posGuid),
          this.masterSaleFormatListRequest(posGuid),
          this.newProductsRequest(posGuid),
          this.groupOfProductPricingListRequest(posGuid),
        ]);
      }
    } catch (error) {
      this.addNotification(createErrorNotification(this.$t('restaurantDataError', { error })));
    } finally {
      await this.setLoading(-1);
    }
    try {
      await this.pointOfSaleSettingsRequest(posGuid);
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('restaurantSettingNotFound')));
    }
  }

  @Watch('$route')
  async onPosGuidChanged(newRoute, oldRoute) {
    window.scrollTo(0, 0);
    if (newRoute.params.pointOfSaleGuid !== oldRoute.params.pointOfSaleGuid) {
      await this.resetPointOfSaleData();
      await this.removeRestaurantNotifications();
      await this.loadRestaurantData();
      await this.showNewProductsNotification();
    }
  }

  @Watch('predictionPeriodList', { immediate: true })
  onPeriodList(newVal, oldVal) {
    if (oldVal !== undefined) {
      this.showNotificationIfNeeded();
    }
  }

  beforeRouteLeave(to, from, next) {
    this.masterProductListCancelToken?.cancel();
    next();
  }
}

</script>
